"use client";

import { COMPANY_EMAILS } from "@repo/common";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  CalendarRange,
  Coins,
  HeartPulse,
  PartyPopper,
  TramFront,
  Utensils,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";

import { Container } from "../../_components/ui/container";
import {
  benefits,
  investors,
  jobs,
  lifeRowOne,
  lifeRowTwo,
  sizesA,
  sizesB,
  stats,
  teamMembers,
  type TileSize,
} from "../_data";

const benefitIcons: Record<
  string,
  React.ComponentType<{ className?: string }>
> = {
  Healthcare: HeartPulse,
  "Competitive compensation": Coins,
  "Relocation support": TramFront,
  "Ground-floor opportunity": CalendarRange,
  "Team events": PartyPopper,
  "Free meals": Utensils,
};

const portraitArrangements = [
  { rotate: "-7deg", translate: "translate-y-3" },
  { rotate: "4deg", translate: "-translate-y-2" },
  { rotate: "-3deg", translate: "translate-y-1" },
  { rotate: "6deg", translate: "-translate-y-3" },
];

function shuffle<T>(arr: readonly T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const ai = a[i];
    const aj = a[j];
    if (ai !== undefined && aj !== undefined) {
      a[i] = aj;
      a[j] = ai;
    }
  }
  return a;
}

export function CareersHero() {
  const [picks, setPicks] = useState<typeof teamMembers>(() =>
    teamMembers.slice(0, 3),
  );

  useEffect(() => {
    setPicks(shuffle(teamMembers).slice(0, 3));
    const interval = setInterval(() => {
      setPicks((prev) => {
        const used = new Set(prev.map((p) => p.src));
        const available = teamMembers.filter((m) => !used.has(m.src));
        if (available.length === 0) {
          return prev;
        }
        const next = [...prev];
        const newMember =
          available[Math.floor(Math.random() * available.length)];
        const idx = Math.floor(Math.random() * prev.length);
        if (newMember) {
          next[idx] = newMember;
        }
        return next;
      });
    }, 2800);
    return () => {
      clearInterval(interval);
    };
  }, []);

  return (
    <section className="relative pt-4 pb-16 md:pt-6 md:pb-20">
      <Container>
        <motion.div
          initial={{ opacity: 0, transform: "translateY(16px)" }}
          animate={{ opacity: 1, transform: "translateY(0px)" }}
          transition={{ duration: 0.5 }}
          className="grid items-end gap-12 lg:grid-cols-12 lg:gap-10"
        >
          <div className="lg:col-span-6 xl:col-span-7">
            <span className="mb-7 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-medium tracking-[-0.01em] text-white/90 backdrop-blur-sm">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              {jobs.length} open roles, hiring across every function
            </span>
            <h1 className="text-4xl leading-[0.95] font-medium tracking-[-2px] text-white sm:text-[52px] md:text-[58px] md:leading-[0.94] md:tracking-[-2.5px] lg:text-[56px] xl:text-[68px] xl:tracking-[-3px]">
              Help us build the labor marketplace for the{" "}
              <span className="text-marketing-blue inline-block rounded-[8px] bg-[#fffff0] [box-decoration-break:clone] px-3 py-0.5 [-webkit-box-decoration-break:clone]">
                post-AGI
              </span>{" "}
              future.
            </h1>
            <p className="mt-7 max-w-xl text-lg leading-relaxed text-white/80">
              AI will do most of the world&apos;s work, but it will still need{" "}
              <span className="font-medium text-white">people</span>. Join us to
              build the infrastructure that lets any team or agent source, hire,
              verify, and pay humans on demand.
            </p>
            <div className="mt-9 flex flex-row flex-wrap items-center gap-3">
              <Link
                href="#open-roles"
                className="text-marketing-blue inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-medium transition-colors hover:bg-white/90"
              >
                View all roles
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                href="https://cal.com/zac/terac"
                className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-white/10"
              >
                Talk to a founder
              </Link>
            </div>
          </div>

          <div className="hidden lg:col-span-6 lg:block xl:col-span-5">
            <div className="flex items-center justify-end gap-1 overflow-visible xl:gap-2">
              {[
                picks[0],
                picks[1],
                { name: "You", src: null as string | null },
                picks[2],
              ].map((member, i) => {
                const a = portraitArrangements[i % portraitArrangements.length];
                const isYou = !member?.src;
                const cardBase = `relative ${a?.translate ?? ""} w-32 shrink-0 rounded-[6px] border p-2 pb-3 transition-transform hover:z-20 hover:scale-105 xl:w-36`;
                const cardSkin = isYou
                  ? "bg-cyan-300/15 border-cyan-300/70 border-dashed z-10"
                  : "bg-marketing-cream-light border-marketing-border";
                return (
                  <div
                    key={isYou ? "you-card" : `slot-${i}`}
                    className={`${cardBase} ${cardSkin}`}
                    style={{ transform: `rotate(${a?.rotate ?? "0deg"})` }}
                  >
                    <div
                      className={`relative aspect-[3/4] overflow-hidden rounded-[3px] ${
                        isYou
                          ? "bg-marketing-blue/30 grid place-items-center"
                          : "bg-marketing-cream"
                      }`}
                    >
                      {isYou ? (
                        <span className="text-[52px] leading-none font-medium text-cyan-300 xl:text-[60px]">
                          ?
                        </span>
                      ) : (
                        <AnimatePresence mode="wait">
                          <motion.div
                            key={member?.src}
                            initial={{ opacity: 0, transform: "scale(0.94)" }}
                            animate={{ opacity: 1, transform: "scale(1)" }}
                            exit={{ opacity: 0, transform: "scale(0.94)" }}
                            transition={{ duration: 0.45, ease: "easeOut" }}
                            className="absolute inset-0"
                          >
                            <Image
                              src={member?.src as string}
                              alt={member?.name ?? ""}
                              fill
                              className="object-cover"
                              sizes="144px"
                            />
                          </motion.div>
                        </AnimatePresence>
                      )}
                    </div>
                    <div className="relative mt-1.5 h-[32px] text-center">
                      {isYou ? (
                        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
                          <p className="text-[12px] leading-none font-medium tracking-[-0.01em] text-cyan-300">
                            You?
                          </p>
                          <p className="text-[9.5px] leading-none tracking-[0.04em] text-cyan-300/70 uppercase">
                            Your role
                          </p>
                        </div>
                      ) : (
                        <AnimatePresence mode="wait">
                          <motion.div
                            key={member?.src}
                            initial={{
                              opacity: 0,
                              transform: "translateY(4px)",
                            }}
                            animate={{
                              opacity: 1,
                              transform: "translateY(0px)",
                            }}
                            exit={{ opacity: 0, transform: "translateY(-4px)" }}
                            transition={{ duration: 0.35, ease: "easeOut" }}
                            className="absolute inset-0 flex flex-col items-center justify-center gap-1"
                          >
                            <p className="text-marketing-text text-[12px] leading-none font-medium tracking-[-0.01em]">
                              {member?.name.split(" ")[0]}
                            </p>
                            <p className="text-marketing-text-muted text-[9.5px] leading-none tracking-[0.04em] uppercase">
                              {(member as { role?: string } | undefined)
                                ?.role ?? "Engineer"}
                            </p>
                          </motion.div>
                        </AnimatePresence>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="mt-7 text-right font-mono text-[11px] tracking-[0.15em] text-white/70 uppercase">
              You? With this great team.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, transform: "translateY(16px)" }}
          animate={{ opacity: 1, transform: "translateY(0px)" }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mt-14 grid grid-cols-2 gap-y-6 border-t border-white/15 pt-8 lg:grid-cols-4"
        >
          {stats.map((stat) => (
            <div key={stat.label} className="flex flex-col gap-1">
              <span className="font-mono text-[11px] tracking-[0.15em] text-white/60 uppercase">
                {stat.label}
              </span>
              <span className="text-[28px] leading-none font-medium tracking-tight text-white tabular-nums md:text-[32px]">
                {stat.value}
              </span>
            </div>
          ))}
        </motion.div>
      </Container>
    </section>
  );
}

export function TeamBanner() {
  return (
    <section className="bg-marketing-cream pt-16 pb-16 sm:pt-20 sm:pb-20 lg:pt-24 lg:pb-24">
      <Container className="max-w-[1280px]!">
        <motion.div
          initial={{ opacity: 0, transform: "translateY(16px)" }}
          whileInView={{ opacity: 1, transform: "translateY(0px)" }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-10 flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <h2 className="text-marketing-text text-[26px]! leading-[1.05] font-medium! tracking-[-0.02em] sm:text-[30px]! lg:text-[34px]!">
              Our team
            </h2>
            <p className="text-marketing-text-muted mt-3 max-w-[52ch] text-[15px] leading-[1.55]">
              {teamMembers.length} people building Terac in SoMa, San Francisco.
              Engineers, operators, and storytellers.
            </p>
          </div>
          <span className="text-marketing-text-secondary text-[13px]">
            SoMa, San Francisco
          </span>
        </motion.div>
      </Container>

      <div className="px-2 sm:px-0">
        <Container className="max-w-[1280px]! px-0! sm:px-6! lg:px-8!">
          <motion.div
            initial={{ opacity: 0, transform: "translateY(20px)" }}
            whileInView={{ opacity: 1, transform: "translateY(0px)" }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="border-marketing-border relative overflow-hidden rounded-[16px] border sm:rounded-[24px]"
          >
            <div className="relative aspect-[4/5] w-full sm:aspect-[16/9] lg:aspect-[21/9]">
              <Image
                src="/marketing/teams/team-group.jpg"
                alt="The Terac team"
                fill
                priority
                className="object-cover"
                sizes="(max-width: 1280px) 100vw, 1280px"
              />
            </div>
          </motion.div>
        </Container>
      </div>
    </section>
  );
}

export function OpenRoles() {
  const departments = Array.from(new Set(jobs.map((j) => j.department)));
  const grouped = departments.map((dept) => ({
    dept,
    roles: jobs.filter((j) => j.department === dept),
  }));

  return (
    <section
      id="open-roles"
      className="bg-marketing-cream scroll-mt-24 pb-20 sm:pb-24 lg:pb-28"
    >
      <Container className="max-w-[1100px]!">
        <motion.div
          initial={{ opacity: 0, transform: "translateY(16px)" }}
          whileInView={{ opacity: 1, transform: "translateY(0px)" }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-10 flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <h2 className="text-marketing-text text-[26px]! leading-[1.05] font-medium! tracking-[-0.02em] sm:text-[30px]! lg:text-[34px]!">
              Open roles
            </h2>
            <p className="text-marketing-text-muted mt-3 max-w-[52ch] text-[15px] leading-[1.55]">
              {jobs.length} positions across engineering, growth, operations,
              and marketing.
            </p>
          </div>
          <span className="text-marketing-text-secondary text-[13px]">
            {jobs.length} open positions
          </span>
        </motion.div>

        <div className="flex flex-col gap-5">
          {grouped.map((group, gi) => (
            <motion.div
              key={group.dept}
              initial={{ opacity: 0, transform: "translateY(14px)" }}
              whileInView={{ opacity: 1, transform: "translateY(0px)" }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: gi * 0.05 }}
              className="border-marketing-border bg-marketing-cream-light overflow-hidden rounded-[20px] border"
            >
              <div className="border-marketing-border-light flex items-baseline justify-between border-b px-6 py-3 sm:px-8">
                <h3 className="text-marketing-text text-[18px]! font-semibold! tracking-[-0.02em] sm:text-[19px]!">
                  {group.dept}
                </h3>
                <span className="text-marketing-text-secondary font-mono text-[11px] tracking-[0.12em] uppercase">
                  {group.roles.length}{" "}
                  {group.roles.length === 1 ? "role" : "roles"}
                </span>
              </div>
              <div>
                {group.roles.map((job, i) => {
                  const isMail = job.href.startsWith("mailto:");
                  return (
                    <Link
                      key={job.title}
                      href={job.href}
                      target={isMail ? undefined : "_blank"}
                      rel={isMail ? undefined : "noopener noreferrer"}
                      className={`group hover:bg-marketing-cream-hover flex flex-wrap items-center justify-between gap-4 px-6 py-3.5 transition-colors sm:px-8 ${
                        i < group.roles.length - 1
                          ? "border-marketing-border-light border-b"
                          : ""
                      }`}
                    >
                      <div className="flex min-w-0 flex-1 flex-col gap-1">
                        <span className="text-marketing-text text-[17px] font-medium tracking-[-0.015em] sm:text-[18px]">
                          {job.title}
                        </span>
                        <span className="text-marketing-text-muted text-[13px]">
                          {job.location} · {job.type}
                        </span>
                      </div>
                      <span className="text-marketing-text-secondary group-hover:text-marketing-blue inline-flex items-center gap-2 text-sm font-medium transition-colors">
                        Apply
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                      </span>
                    </Link>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, transform: "translateY(14px)" }}
          whileInView={{ opacity: 1, transform: "translateY(0px)" }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="border-marketing-border mt-5 flex flex-wrap items-center justify-between gap-4 rounded-[20px] border border-dashed px-6 py-5 sm:px-8"
        >
          <div className="flex flex-col gap-1">
            <span className="text-marketing-text text-[15px] font-medium tracking-[-0.01em]">
              Don&apos;t see your role?
            </span>
            <span className="text-marketing-text-muted text-[13.5px]">
              We&apos;re always looking for exceptional people.
            </span>
          </div>
          <Link
            href={`mailto:${COMPANY_EMAILS.careers}?subject=General Application`}
            className="text-marketing-blue inline-flex items-center gap-2 text-sm font-medium hover:underline"
          >
            {COMPANY_EMAILS.careers}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </Container>
    </section>
  );
}

export function Benefits() {
  return (
    <section className="bg-marketing-cream pb-20 sm:pb-24 lg:pb-28">
      <Container className="max-w-[1100px]!">
        <motion.div
          initial={{ opacity: 0, transform: "translateY(16px)" }}
          whileInView={{ opacity: 1, transform: "translateY(0px)" }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-12 flex flex-wrap items-end justify-between gap-6"
        >
          <div className="max-w-[52ch]">
            <h2 className="text-marketing-text text-[26px]! leading-[1.05] font-medium! tracking-[-0.02em] sm:text-[30px]! lg:text-[34px]!">
              Benefits
            </h2>
            <p className="text-marketing-text-muted mt-3 text-[15px] leading-[1.55]">
              Everything you need to do your best work, plus a few things that
              make Terac feel like home.
            </p>
          </div>
          <span className="text-marketing-text-secondary text-[13px]">
            For full-time employees in San Francisco
          </span>
        </motion.div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {benefits.map((b, i) => {
            const Icon = benefitIcons[b.title] ?? Coins;
            return (
              <motion.div
                key={b.title}
                initial={{ opacity: 0, transform: "translateY(12px)" }}
                whileInView={{ opacity: 1, transform: "translateY(0px)" }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="border-marketing-border bg-marketing-cream-light hover:border-marketing-blue/30 flex flex-col gap-3 rounded-[16px] border p-5 transition-colors sm:p-6"
              >
                <span className="bg-marketing-card-blue text-marketing-blue inline-flex h-9 w-9 items-center justify-center rounded-full">
                  <Icon className="h-4 w-4" />
                </span>
                <div className="flex flex-1 flex-col">
                  <h3 className="text-marketing-text text-[17px]! font-medium! tracking-[-0.015em] sm:text-[18px]!">
                    {b.title}
                  </h3>
                  <p className="text-marketing-text-muted mt-1.5 text-[13.5px] leading-[1.55]">
                    {b.body}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Container>
    </section>
  );
}

function LifeTile({
  src,
  label,
  size,
}: {
  src: string;
  label: string;
  size: TileSize;
}) {
  const widths: Record<TileSize, string> = {
    sm: "w-[240px] sm:w-[260px]",
    md: "w-[300px] sm:w-[340px]",
    lg: "w-[380px] sm:w-[440px]",
    xl: "w-[460px] sm:w-[540px]",
  };
  return (
    <div
      className={`border-marketing-border bg-marketing-cream-light relative h-[260px] shrink-0 overflow-hidden rounded-[20px] border sm:h-[300px] ${widths[size]}`}
    >
      <Image
        src={src}
        alt={label}
        fill
        className="object-cover"
        sizes="540px"
      />
    </div>
  );
}

const BURST_THRESHOLD = 4;

function arrangeWithoutAdjacentSimilar<T extends { src: string }>(
  items: T[],
): T[] {
  if (items.length < 2) {
    return [...items];
  }
  const idx = (src: string) => {
    const m = src.match(/life-(\d+)/);
    return m ? Number(m[1]) : 0;
  };
  const tooClose = (a: T | undefined, b: T | undefined) => {
    if (!a || !b) {
      return false;
    }
    return Math.abs(idx(a.src) - idx(b.src)) <= BURST_THRESHOLD;
  };

  const out = [...items].sort(() => Math.random() - 0.5);
  for (let pass = 0; pass < 6; pass++) {
    let swapped = false;
    for (let i = 1; i < out.length; i++) {
      if (!tooClose(out[i - 1], out[i])) {
        continue;
      }
      let candidate = -1;
      for (let j = i + 1; j < out.length; j++) {
        if (!tooClose(out[i - 1], out[j]) && !tooClose(out[j - 1], out[i])) {
          candidate = j;
          break;
        }
      }
      if (candidate === -1) {
        for (let j = i + 1; j < out.length; j++) {
          if (!tooClose(out[i - 1], out[j])) {
            candidate = j;
            break;
          }
        }
      }
      if (candidate !== -1) {
        const tmp = out[i];
        const swap = out[candidate];
        if (tmp && swap) {
          out[i] = swap;
          out[candidate] = tmp;
          swapped = true;
        }
      }
    }
    if (!swapped) {
      break;
    }
  }

  for (let attempt = 0; attempt < out.length; attempt++) {
    const last = out[out.length - 1];
    const first = out[0];
    if (!tooClose(last, first)) {
      break;
    }
    for (let j = out.length - 2; j > 0; j--) {
      const candidate = out[j];
      const prev = out[j - 1];
      if (!tooClose(candidate, first) && !tooClose(prev, last)) {
        out[out.length - 1] = candidate!;
        out[j] = last!;
        break;
      }
    }
  }
  return out;
}

export function LifeAtTerac() {
  const [rows, setRows] = useState<{
    rowOne: typeof lifeRowOne;
    rowTwo: typeof lifeRowTwo;
  }>({ rowOne: lifeRowOne, rowTwo: lifeRowTwo });

  useEffect(() => {
    const all = [...lifeRowOne, ...lifeRowTwo];
    const mixed = arrangeWithoutAdjacentSimilar(all);
    const half = Math.ceil(mixed.length / 2);
    setRows({ rowOne: mixed.slice(0, half), rowTwo: mixed.slice(half) });
  }, []);

  return (
    <section className="bg-marketing-cream overflow-hidden pb-20 sm:pb-24 lg:pb-28">
      <Container className="max-w-[1280px]!">
        <motion.div
          initial={{ opacity: 0, transform: "translateY(16px)" }}
          whileInView={{ opacity: 1, transform: "translateY(0px)" }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-12 max-w-[52ch]"
        >
          <h2 className="text-marketing-text text-[26px]! leading-[1.05] font-medium! tracking-[-0.02em] sm:text-[30px]! lg:text-[34px]!">
            Life at Terac
          </h2>
          <p className="text-marketing-text-muted mt-3 text-[15px] leading-[1.55]">
            A tight-knit crew in San Francisco that moves fast, sweats the
            details, and likes being around each other.
          </p>
        </motion.div>
      </Container>

      <div className="space-y-4">
        <div className="careers-marquee-mask group overflow-hidden">
          <div className="careers-marquee-track flex w-max gap-4 group-hover:[animation-play-state:paused]">
            {["a", "b"].flatMap((copy) =>
              rows.rowOne.map((item, idx) => (
                <LifeTile
                  key={`row1-${copy}-${item.src}`}
                  src={item.src}
                  label={item.label}
                  size={sizesA[idx % sizesA.length] ?? "md"}
                />
              )),
            )}
          </div>
        </div>

        <div className="careers-marquee-mask group overflow-hidden">
          <div className="careers-marquee-track-reverse flex w-max gap-4 group-hover:[animation-play-state:paused]">
            {["a", "b"].flatMap((copy) =>
              rows.rowTwo.map((item, idx) => (
                <LifeTile
                  key={`row2-${copy}-${item.src}`}
                  src={item.src}
                  label={item.label}
                  size={sizesB[idx % sizesB.length] ?? "md"}
                />
              )),
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .careers-marquee-mask {
          -webkit-mask-image: linear-gradient(
            90deg,
            transparent,
            #000 6%,
            #000 94%,
            transparent
          );
          mask-image: linear-gradient(
            90deg,
            transparent,
            #000 6%,
            #000 94%,
            transparent
          );
        }
        .careers-marquee-track {
          animation: careers-scroll-left 60s linear infinite;
        }
        .careers-marquee-track-reverse {
          animation: careers-scroll-right 70s linear infinite;
        }
        @keyframes careers-scroll-left {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(-50%);
          }
        }
        @keyframes careers-scroll-right {
          from {
            transform: translateX(-50%);
          }
          to {
            transform: translateX(0);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .careers-marquee-track,
          .careers-marquee-track-reverse {
            animation: none;
          }
        }
      `}</style>
    </section>
  );
}

export function Investors() {
  return (
    <section className="bg-marketing-cream pb-20 sm:pb-24 lg:pb-28">
      <Container className="max-w-[1100px]!">
        <motion.div
          initial={{ opacity: 0, transform: "translateY(16px)" }}
          whileInView={{ opacity: 1, transform: "translateY(0px)" }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-10 flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <h2 className="text-marketing-text text-[26px]! leading-[1.05] font-medium! tracking-[-0.02em] sm:text-[30px]! lg:text-[34px]!">
              Backed by world-class investors
            </h2>
            <p className="text-marketing-text-muted mt-3 max-w-[52ch] text-[15px] leading-[1.55]">
              $9M raised to build the supply layer for the post-AGI economy.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, transform: "translateY(14px)" }}
          whileInView={{ opacity: 1, transform: "translateY(0px)" }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="border-marketing-border bg-marketing-cream-light grid grid-cols-2 overflow-hidden rounded-[20px] border sm:grid-cols-3 lg:grid-cols-6"
        >
          {investors.map((investor, i) => (
            <Link
              key={investor.name}
              href={investor.href}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center justify-center px-6 py-8 opacity-80 transition-opacity hover:opacity-100 ${
                i % 2 === 0 && i + 1 < investors.length
                  ? "border-marketing-border-light border-r sm:border-r-0"
                  : ""
              } ${
                i + 1 < investors.length && (i + 1) % 3 !== 0 && i % 3 !== 2
                  ? "sm:border-marketing-border-light sm:border-r lg:border-r-0"
                  : ""
              } ${
                i + 1 < investors.length
                  ? "lg:border-marketing-border-light lg:border-r"
                  : ""
              } ${
                i < investors.length - 2
                  ? "border-marketing-border-light border-b sm:border-b-0"
                  : ""
              } ${
                i < 3
                  ? "border-marketing-border-light sm:border-b lg:border-b-0"
                  : ""
              }`}
            >
              <Image
                src={investor.image}
                alt={`${investor.name} logo`}
                width={160}
                height={40}
                className="h-7 w-auto max-w-[120px] object-contain sm:h-8"
              />
            </Link>
          ))}
        </motion.div>
      </Container>
    </section>
  );
}

export function CareersCTA() {
  return (
    <section className="bg-marketing-cream px-4 pb-16 sm:pb-20">
      <div className="bg-marketing-text relative mx-auto max-w-[1466px] overflow-hidden rounded-[28px] px-6 py-20 text-center text-[#fffff0] sm:px-12 sm:py-24 lg:py-28">
        <h2 className="mx-auto max-w-[24ch] text-[28px] leading-[1.05] font-medium tracking-[-0.02em] text-[#fffff0] sm:max-w-[20ch] sm:text-[48px] sm:leading-[0.98] sm:tracking-[-0.03em] lg:text-[60px]">
          Come build{" "}
          <span className="whitespace-nowrap">
            what&apos;s{" "}
            <span className="rounded-[8px] bg-[#0a06f7] [box-decoration-break:clone] px-3 py-0.5 text-[#fffff0] [-webkit-box-decoration-break:clone]">
              next
            </span>
            .
          </span>
        </h2>
        <p
          className="mx-auto mt-6 max-w-[52ch] text-base leading-[1.6] sm:text-lg"
          style={{ color: "rgba(255,255,240,0.78)" }}
        >
          The supply layer for human labor is being written from scratch. Your
          fingerprints will be on the product that changes how the world learns,
          decides, and builds.
        </p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <Link
            href="#open-roles"
            className="text-marketing-text inline-flex items-center gap-2 rounded-full bg-[#fffff0] px-6 py-3 text-sm font-medium tracking-[-0.01em] transition-opacity hover:opacity-90"
          >
            View open roles
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link
            href="https://cal.com/zac/terac"
            className="inline-flex items-center gap-2 rounded-full border px-6 py-3 text-sm font-medium tracking-[-0.01em] text-[#fffff0] transition-colors hover:bg-white/10"
            style={{ borderColor: "rgba(255,255,240,0.4)" }}
          >
            Talk to the founders
          </Link>
        </div>
      </div>
    </section>
  );
}
